import { logger } from './logger';
import api from './api';

const MAX_PDF_SIZE_BYTES = 20 * 1024 * 1024; // 20MB
const MAX_LOGO_SIZE_BYTES = 5 * 1024 * 1024; // 5MB

const ALLOWED_LOGO_TYPES = new Set(['image/png', 'image/jpeg', 'image/svg+xml']);

/**
 * Build the owner/catalog query params shared by every CV-asset request.
 * Returns a fresh object (never mutates its inputs).
 *
 * `catalogAliId` scopes the asset to one of the user's OWNED catalogs
 * (primary keeps the legacy key, add-ons get a per-catalog key). It is only
 * sent for own-catalog requests; delegated requests use `ownerId` instead.
 */
const assetParams = (ownerId?: string, catalogAliId?: string): Record<string, string> => ({
  ...(ownerId ? { owner_id: ownerId } : {}),
  ...(catalogAliId ? { catalogAliId } : {}),
});

// ---------------------------------------------------------------------------
// CV PDF
// ---------------------------------------------------------------------------

const getCvPdfUploadUrl = async (
  ownerId?: string,
  catalogAliId?: string
): Promise<{ uploadUrl: string; bucket: string; key: string }> => {
  const response = await api.get('/api/cv/pdf/upload-url', {
    params: {
      content_type: 'application/pdf',
      ...assetParams(ownerId, catalogAliId),
    },
  });
  return response.data;
};

export const uploadCvPdf = async (
  file: File,
  ownerId?: string,
  catalogAliId?: string
): Promise<{ pdfUrl: string }> => {
  if (file.type !== 'application/pdf') {
    throw new Error('Only PDF files are allowed');
  }
  if (file.size > MAX_PDF_SIZE_BYTES) {
    throw new Error('PDF file must be smaller than 20MB');
  }

  try {
    const { uploadUrl } = await getCvPdfUploadUrl(ownerId, catalogAliId);

    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': 'application/pdf' },
    });

    if (!uploadResponse.ok) {
      const errorText = await uploadResponse.text();
      logger.error('[CV PDF] S3 upload failed', {
        status: uploadResponse.status,
        error: errorText,
      });
      throw new Error(`S3 upload failed: ${uploadResponse.status}`);
    }

    const pdfData = await getCvPdfUrl(ownerId, catalogAliId);
    if (!pdfData) {
      throw new Error('Failed to get CV PDF URL after upload');
    }
    return pdfData;
  } catch (error) {
    const axiosError = error as { message?: string; response?: { status?: number } };
    logger.error('[CV PDF] Upload failed', { message: axiosError.message });
    throw error;
  }
};

export const getCvPdfUrl = async (
  ownerId?: string,
  catalogAliId?: string
): Promise<{ pdfUrl: string } | null> => {
  try {
    const response = await api.get('/api/cv/pdf', {
      params: assetParams(ownerId, catalogAliId),
    });
    return response.data;
  } catch (error) {
    const axiosError = error as { response?: { status?: number } };
    if (axiosError.response?.status === 404) {
      return null;
    }
    logger.error('[CV PDF] Failed to fetch URL', { error });
    throw error;
  }
};

export const deleteCvPdf = async (ownerId?: string, catalogAliId?: string): Promise<void> => {
  try {
    await api.delete('/api/cv/pdf', {
      params: assetParams(ownerId, catalogAliId),
    });
  } catch (error) {
    logger.error('[CV PDF] Delete failed', { error });
    throw error;
  }
};

export const getPublicCvPdfUrl = async (
  aliId: string
): Promise<{ pdfUrl: string } | null> => {
  try {
    const response = await api.get(`/api/cv/pdf/public/${aliId}`);
    return response.data;
  } catch (error) {
    const axiosError = error as { response?: { status?: number } };
    if (axiosError.response?.status === 404) {
      return null;
    }
    logger.error('[CV PDF] Failed to fetch public URL', { error });
    throw error;
  }
};

// ---------------------------------------------------------------------------
// Logo
// ---------------------------------------------------------------------------

const getCvLogoUploadUrl = async (
  contentType: string,
  ownerId?: string,
  catalogAliId?: string
): Promise<{ uploadUrl: string; bucket: string; key: string }> => {
  const response = await api.get('/api/cv/logo/upload-url', {
    params: {
      content_type: contentType,
      ...assetParams(ownerId, catalogAliId),
    },
  });
  return response.data;
};

export const uploadCvLogo = async (
  file: File,
  ownerId?: string,
  catalogAliId?: string
): Promise<{ logoUrl: string }> => {
  if (!ALLOWED_LOGO_TYPES.has(file.type)) {
    throw new Error('Only PNG, JPEG, and SVG files are allowed for logos');
  }
  if (file.size > MAX_LOGO_SIZE_BYTES) {
    throw new Error('Logo file must be smaller than 5MB');
  }

  try {
    const { uploadUrl } = await getCvLogoUploadUrl(file.type, ownerId, catalogAliId);

    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type },
    });

    if (!uploadResponse.ok) {
      const errorText = await uploadResponse.text();
      logger.error('[Logo] S3 upload failed', {
        status: uploadResponse.status,
        error: errorText,
      });
      throw new Error(`S3 upload failed: ${uploadResponse.status}`);
    }

    const logoData = await getCvLogoUrl(ownerId, catalogAliId);
    if (!logoData) {
      throw new Error('Failed to get logo URL after upload');
    }
    return logoData;
  } catch (error) {
    const axiosError = error as { message?: string; response?: { status?: number } };
    logger.error('[Logo] Upload failed', { message: axiosError.message });
    throw error;
  }
};

export const getCvLogoUrl = async (
  ownerId?: string,
  catalogAliId?: string
): Promise<{ logoUrl: string } | null> => {
  try {
    const response = await api.get('/api/cv/logo', {
      params: assetParams(ownerId, catalogAliId),
    });
    return response.data;
  } catch (error) {
    const axiosError = error as { response?: { status?: number } };
    if (axiosError.response?.status === 404) {
      return null;
    }
    logger.error('[Logo] Failed to fetch URL', { error });
    throw error;
  }
};

export const deleteCvLogo = async (ownerId?: string, catalogAliId?: string): Promise<void> => {
  try {
    await api.delete('/api/cv/logo', {
      params: assetParams(ownerId, catalogAliId),
    });
  } catch (error) {
    logger.error('[Logo] Delete failed', { error });
    throw error;
  }
};

export const getPublicCvLogoUrl = async (
  aliId: string
): Promise<{ logoUrl: string } | null> => {
  try {
    const response = await api.get(`/api/cv/logo/public/${aliId}`);
    return response.data;
  } catch (error) {
    const axiosError = error as { response?: { status?: number } };
    if (axiosError.response?.status === 404) {
      return null;
    }
    logger.error('[Logo] Failed to fetch public URL', { error });
    throw error;
  }
};
